/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ForgotPasswordComponent } from '../components/accounts/forgot-password.component';
import { BasicAlertComponent } from '../components/alerts/basic-alert.component';
import { ActivityIndicatorModalComponent } from '../components/modals/activity-indicator-modal.component';
import { UserService } from './user.service';
import { XdaStringsService } from './xda-strings.service';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private loginWindow: Window | null = null;
  private loginUrl: string;
  private mfdLoginUrl: string;
  private createAccountUrl: string;
  private agBaseUrl: string;
  private loginCheckInterval: number | undefined;
  private loginCompleted = false;

  constructor(
    private xdaStringsService: XdaStringsService,
    private modalService: NgbModal,
    private userService: UserService,
  ) {
    // attempt to get app gallery version while we are initializing everything
    userService.getAGVersion();
    this.agBaseUrl = this.xdaStringsService.strings['AGBASEURL'].slice(0, -1);
    const appId = this.xdaStringsService.strings['APPID'];
    const redirectUrl = window.location.href.split('?')[0];
    this.loginUrl = `${this.agBaseUrl}/#!/login?app=${appId}`;
    this.mfdLoginUrl = `${this.agBaseUrl}/areas/appgalleryapp/#!/all-apps?app=${encodeURIComponent(appId)}&redirectUrl=${encodeURIComponent(redirectUrl)}`;
    this.createAccountUrl = `${this.agBaseUrl}/#!/create-account?appId=${appId}`;
  }

  public async initiateWebLogin() {
    this.loginCompleted = false;
    this.openLoginWindow(this.loginUrl);
  }

  public async initiateMFDLogin() {
    window.location.href = this.mfdLoginUrl;
  }

  private openLoginWindow(url: string) {
    // remove any left over listener
    window.removeEventListener('message', this.receiveLoginMessage, false);
    
    // Clear any existing interval
    if (this.loginCheckInterval !== undefined) {
      clearInterval(this.loginCheckInterval);
      this.loginCheckInterval = undefined;
    }

    const height = 700;
    const width = 600;
    const left = screen.width / 2 - width / 2;
    const top = screen.height / 2 - height / 2;
    const screenX = window.screenX || 0;

    // window features
    const features = `toolbar=no, menubar=no, width=${width}, height=${height}, top=${top}, left=${left + screenX}`;

    // If new window just open
    if (this.loginWindow == null || this.loginWindow.closed) {
      this.loginWindow = window.open(url, 'login window', features);
    } else {
      // else just focus
      this.loginWindow.focus();
    }

    // add listener for receiving tokens
    window.addEventListener('message', this.receiveLoginMessage, false);
    
    // Monitor if popup window is closed without completing login
    this.loginCheckInterval = window.setInterval(() => {
      if (this.loginWindow && this.loginWindow.closed && !this.loginCompleted) {
        if (this.loginCheckInterval !== undefined) {
          clearInterval(this.loginCheckInterval);
          this.loginCheckInterval = undefined;
        }
        window.removeEventListener('message', this.receiveLoginMessage, false);
        this.handleLoginCanceled();
      }
    }, 500);
  }
  
  private handleLoginCanceled() {
    // User closed the login popup without completing login
    // Check if user was previously logged in
    if (this.userService.isLoggedOn()) {
      // User was logged in but canceled EULA or login process
      // Log them out completely
      this.userService.user = null;
      this.userService.setCookie('__Secure-user', '', -7);
      sessionStorage.removeItem('refreshToken');
      this.modalService.dismissAll('');
      this.userService.redirectToHome();
      this.userService.userLoggedOff.emit(true);
    }
    // If user was not logged in, no action needed
  }

  private receiveLoginMessage = async (message: MessageEvent) => {
    //Only listen for messages from appgallery
    if (message.origin !== this.agBaseUrl) return;

    if (message && message.data) {
      // When we login we are no longer a guest user
      this.userService.isGuestUser = false;

      if (message.data.action == 'create-account') {
        this.createAccount();
        window.removeEventListener('message', this.receiveLoginMessage, false);
        if (this.loginCheckInterval !== undefined) {
          clearInterval(this.loginCheckInterval);
          this.loginCheckInterval = undefined;
        }
      } else if (message.data.status == 'complete') {
        this.loginCompleted = true; // Mark login as completed
        
        if (this.loginCheckInterval !== undefined) {
          clearInterval(this.loginCheckInterval);
          this.loginCheckInterval = undefined;
        }
        
        const refreshToken = message.data.tokens?.refresh;
        const authCode = message.data.authCode || "";

        // If user info is required app gallery will give us the action edit-account
        if (message.data.action == 'edit-account') {
          const modalRef = this.modalService.open(BasicAlertComponent);
          modalRef.componentInstance.title = 'SDE_ACCOUNT_MISSING_SOME';
          modalRef.componentInstance.buttonText = 'SDE_CLOSE';
          modalRef.result.then(async () => {
            const agBaseUrl = this.xdaStringsService.strings['AGBASEURL'];
            let redirectUrl = "";
            if (refreshToken) {
              const appId = this.xdaStringsService.strings['APPID'];
              redirectUrl = `${agBaseUrl}#!/edit-account?appId=${appId}&refreshToken=${refreshToken}`;
            } else {
              const baseUrl = `${agBaseUrl}#!/edit-account`;
              redirectUrl = await this.userService.createAuthCodeRedirect(baseUrl);
            }
            window.location.href = redirectUrl;
          });
        } else {
          // Show a spinner so the user knows we are still working
          this.modalService.open(ActivityIndicatorModalComponent, {
            windowClass: 'xrx-modal transparent',
            centered: true,
            backdrop: 'static',
          });
          refreshToken ?
            await this.userService.refreshToken(refreshToken) :
            await this.userService.exchangeAuthCode(authCode);
        }
        window.removeEventListener('message', this.receiveLoginMessage, false);
      }
    }
  };

  // Returns 1 if v1 is newer than v2
  // Returns 0 if they are the same
  // Returns -1 if v1 is older than v2
  compareReleaseVersions(v1: string, v2: string) {
    const v1Split = v1.split('.');
    const v2Split = v2.split('.');

    for (let i = 0; i < v1Split.length; i++) {
      const v1Num = parseInt(v1Split[i]) || 0;
      const v2Num = parseInt(v2Split[i]) || 0;

      if (v1Num > v2Num) {
        return 1;
      }
      if (v1Num < v2Num) {
        return -1;
      }
    }
    return 0;
  }

  createAccount() {
    window.location.href = this.createAccountUrl;
  }

  forgotPassword() {
    this.modalService.open(ForgotPasswordComponent, {
      windowClass: 'xrx-modal',
    });
  }
}
